import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  TextField
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { addExpense } from 'redux/actions/index';
import { Employee_Salary, Employee_Expense, Vendor_Expense, Vendor_Expense_Admin, Petty_Expense } from 'constants/expenseTypes';
import { VAT_UAE_RECIPROCAL } from 'constants/globalVAT';
import { standard } from 'constants/userAccountTypes';
import { months } from 'constants/months';

const standardExpenseTypes = [
  { firstLetter: "P", type: Petty_Expense, vatable: false }
];

const adminExpenseTypes = [
  { firstLetter: "C", type: "Crystal", vatable: true },
  { firstLetter: "D", type: "Document Work", vatable: true },
  { firstLetter: "E", type: Employee_Expense, vatable: false },
  { firstLetter: "E", type: Employee_Salary, vatable: false },
  { firstLetter: "M", type: "Material and Cloth", vatable: true },
  { firstLetter: "O", type: "Order Alter", vatable: false },
  { firstLetter: "P", type: Petty_Expense, vatable: false },
  { firstLetter: "U", type: "Utilities", vatable: true },
  { firstLetter: "V", type: Vendor_Expense, vatable: false },
  { firstLetter: "V", type: Vendor_Expense_Admin, vatable: false }
];

const ExpenseDetails = (props) => {

  const [typeInput, setTypeInput] = useState({firstLetter: "", type: "", vatable: false});
  const [employeeInput, setEmployeeInput] = useState({firstLetter: "", id: 0, displayId: "", name: ""});
  const [vendorInput, setVendorInput] = useState({firstLetter: "", id: 0, displayId: "", name: ""});
  const [userInput, setUserInput] = useState({firstLetter: "", id: "", name: ""});

  // If editing Expenses is enabled, the previous total(props.expenseInfo.total)
  // will contain VAT may need to be subtracted, update accordingly
  const [values, setValues] = useState({
    description: "",
    total: "",
    month: "0",
    year: moment().year().toString()
  });

  const employeesObj = useMemo(() => (
    props.employees
      .map(employee => {
        const firstLetter = employee.name[0].toUpperCase();
        return {firstLetter, id: employee.id, displayId: employee.displayId, name: employee.name}
      })
      .sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter))
  
  ), [props.employees]);

  const vendorsObj = useMemo(() => (
    props.vendors
      .map(vendor => {
        const firstLetter = vendor.name[0].toUpperCase();
        return {firstLetter, id: vendor.id, displayId: vendor.displayId, name: vendor.name}
      })
      .sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter))
  
  ), [props.vendors]);

  const usersObj = useMemo(() => (
    props.user.standardUsers
      .map(user => {
        const firstLetter = user.name[0].toUpperCase();
        return {firstLetter, id: user.id, name: user.name}
      })
      .sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter))

  ), [props.user.standardUsers]);

  const handleChange = (event) => {
    setValues({
      ...values,
      [event.target.name]: event.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const addToSelf = [ Petty_Expense, Employee_Salary, Employee_Expense ].includes(typeInput.type);
    
    const newExpense = {
      addToSelf,
      ...( !addToSelf && {userId: userInput.id} ),
      expense: {
        displayId: props.createExpenseDisplayId( addToSelf, userInput.id ),
        type: typeInput.type,
        description: values.description,
        total: values.total,
        ...( typeInput.vatable && {totalVat: values.total - Math.round( values.total / VAT_UAE_RECIPROCAL )} ),
        ...( typeInput.type === Employee_Expense && {employeeId: employeeInput.id} ),
        ...( typeInput.type === Employee_Salary && {monthYear: new Date(values.year, values.month), employeeId: employeeInput.id} ),
        ...( [Vendor_Expense, Vendor_Expense_Admin].includes(typeInput.type) && {monthYear: new Date(values.year, values.month), vendorId: vendorInput.id} )
      }
    };
    props.addExpense(newExpense, props.user.adminId);
    
    if([ Employee_Salary, Vendor_Expense, Vendor_Expense_Admin ].includes(typeInput.type)) {
      const isSalary = typeInput.type === Employee_Salary;

      const invoiceInfo = {
        isSalary,
        displayId: newExpense.expense.displayId,
        payeeDisplayId: isSalary ? employeeInput.displayId : vendorInput.displayId,
        payeeName: isSalary ? employeeInput.name : vendorInput.name,
        monthYear: newExpense.expense.monthYear,
        paymentDate: new Date(),
        description: values.description,
        total: values.total
      };
      props.openModal(invoiceInfo);
    }
    
    expenseCleanup();
  };

  const expenseCleanup = () => {
    setTypeInput({firstLetter: "", type: "", vatable: false});
    setEmployeeInput({firstLetter: "", id: 0, displayId: "", name: ""});
    setVendorInput({firstLetter: "", id: 0, displayId: "", name: ""});
    setUserInput({firstLetter: "", id: "", name: ""});
    setValues({
      description: "",
      total: "",
      month: "0",
      year: moment().year().toString()
    });

    // If Cancelled or Completed, redirect to ExpensesTable.
    props.setTabValue(0);
  };

  const getRequiredFields = (expenseType) => {
    switch(expenseType) {
      case Employee_Salary:
        return ['employee', 'monthYear'];
      case Employee_Expense:
        return ['employee'];
      case Vendor_Expense: 
      case Vendor_Expense_Admin:
        return ['user', 'vendor', 'monthYear'];
      case Petty_Expense:
        return [];
      default:
        return props.activeUserType === standard ? [] : ['user'];
    }
  };

  const conditionalRender = {
    employee: (
      <Grid
        item
        md={6}
        xs={12}
        key={"employee"}
      >
        <Autocomplete
          onChange={(event, newValue) => {
            if(newValue) setEmployeeInput(newValue);
            else setEmployeeInput({firstLetter: "", id: 0, displayId: "", name: ""});
          }}
          id="free-solo-dialog-demo"
          options={employeesObj}
          groupBy={(employee) => employee.firstLetter}
          getOptionSelected={(option, value) => option.id === value.id}
          getOptionLabel={(option) => option.name}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label="Select Employee.."
              variant="outlined"
              helperText="Please Select the Employee"
            />
          )}
          value={employeeInput ? ( employeeInput.id ? employeeInput : null ) : null}
        />
      </Grid>
    ),
    vendor: (
      <Grid
        item
        md={6}
        xs={12}
        key={"vendor"}
      >
        <Autocomplete
          onChange={(event, newValue) => {
            if(newValue) setVendorInput(newValue);
            else setVendorInput({firstLetter: "", id: 0, displayId: "", name: ""});
          }}
          id="free-solo-dialog-demo"
          options={vendorsObj}
          groupBy={(vendor) => vendor.firstLetter}
          getOptionSelected={(option, value) => option.id === value.id}
          getOptionLabel={(option) => option.name}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label="Select Vendor.."
              variant="outlined"
              helperText="Please Select the Vendor"
            />
          )}
          value={vendorInput ? ( vendorInput.id ? vendorInput : null ) : null}
        />
      </Grid>
    ),
    user: (
      <Grid
        item
        md={6}
        xs={12}
        key={"user"}
      >
        <Autocomplete
          onChange={(event, newValue) => {
            if(newValue) setUserInput(newValue);
            else setUserInput({firstLetter: "", id: "", name: ""});
          }}
          id="free-solo-dialog-demo"
          options={usersObj}
          groupBy={(user) => user.firstLetter}
          getOptionSelected={(option, value) => option.id === value.id}
          getOptionLabel={(option) => option.name}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label="Select User.."
              variant="outlined"
              helperText="Please Select the User"
            />
          )}
          value={userInput ? ( userInput.id ? userInput : null ) : null}
        />
      </Grid>
    ),
    monthYear: (
      <React.Fragment key={"monthYear"}>
        <Grid
          item
          md={6}
          xs={12}
          key={"month"}
        >
          <TextField
            fullWidth
            helperText="Please select the Month"
            label="Select Month"
            name="month"
            onChange={handleChange}
            select
            SelectProps={{ native: true }}
            value={values.month}
            variant="outlined"
          >
            {months.map((option) => (
              <option
                key={option.value}
                value={option.value}
              >
                {option.label}
              </option>
            ))}
          </TextField>
        </Grid>
        <Grid
          item
          md={6}
          xs={12}
          key={"year"}
        >
          <TextField
            fullWidth
            helperText="Please specify the Year"
            label="Year"
            name="year"
            onChange={handleChange}
            required
            value={values.year}
            variant="outlined"
          />
        </Grid>
      </React.Fragment>
    )
  };

  const requiredFields = getRequiredFields(typeInput.type);

  return (
    <form
      autoComplete="off"
      onSubmit={handleSubmit}
    >
      <Card>
        <CardHeader
          subheader="This will add a New Expense."
          title="New Expense"
        />
        <Divider />
        <CardContent>
          <Grid
            container
            spacing={3}
          >
            <Grid
              item
              md={6}
              xs={12}
            >
              <Autocomplete
                onChange={(event, newValue) => {
                  if(newValue) setTypeInput(newValue);
                  else setTypeInput({firstLetter: "", type: "", vatable: false});
                }}
                id="free-solo-dialog-demo"
                options={props.activeUserType === standard ? standardExpenseTypes : adminExpenseTypes}
                groupBy={(expenseType) => expenseType.firstLetter}
                getOptionSelected={(option, value) => option.type === value.type}
                getOptionLabel={(option) => option.type}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Select Type.."
                    variant="outlined"
                    helperText="Please Select the Expense Type"
                  />
                )}
                value={typeInput ? ( typeInput.type ? typeInput : null ) : null}
              />
            </Grid>
            { requiredFields.map(field => conditionalRender[field]) }
            <Grid
              item
              md={6}
              xs={12}
            >
              <TextField
                fullWidth
                helperText="Please Describe the Expense"
                label="Description"
                name="description"
                onChange={handleChange}
                required
                value={values.description}
                variant="outlined"
              />
            </Grid>
            <Grid
              item
              md={6}
              xs={12}
            >
              <TextField
                fullWidth
                helperText="Please specify the Expense Amount (VAT Included)"
                label="Total Amount"
                onChange={(event) => setValues({ ...values, total: parseFloat(event.target.value) || 0 })}
                required
                value={values.total}
                variant="outlined"
              />
            </Grid>
          </Grid>
        </CardContent>
        <Divider />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            p: 2
          }}
        >
          <Button
            color="primary"
            variant="contained"
            type="submit"
          >
            Save details
          </Button>
          <Button sx={{ mx: 1 }} onClick={expenseCleanup}>
            Cancel
          </Button>
        </Box>
      </Card>
    </form>
  )
};

export default connect (null, {addExpense}) (ExpenseDetails);